import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import type { GetCompanyQuery } from 'garaje/graphql/generated/roomba-types';
import type TakeoverModel from 'garaje/models/takeover';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type RoombaGraphqlService from 'garaje/services/roomba-graphql';
import type RoomsPollingService from 'garaje/services/rooms-polling';
import type StateService from 'garaje/services/state';

interface RoombaListModel {
  company: GetCompanyQuery['company'];
  takeovers: TakeoverModel[];
}

export default class RoombaListRoute extends Route {
  @service declare store: Store;
  @service declare state: StateService;
  @service declare roombaGraphql: RoombaGraphqlService;
  @service declare roomsPolling: RoomsPollingService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare featureConfig: FeatureConfigService;
  @service declare router: RouterService;
  @service declare abilities: AbilitiesService;

  beforeModel(): void {
    if (this.abilities.can('use generic resource')) {
      void this.router.transitionTo('resources.resource-type.index', 3);
    }
  }

  async model(): Promise<RoombaListModel> {
    await this.roomsPolling.fetchRooms.perform();

    const takeoversPromise = this.featureConfig.isEnabled('communications.takeovers')
      ? this.store
          .query('takeover', {
            filter: {
              location: this.state.currentLocation.id,
            },
          })
          .then((takeovers) => takeovers.slice())
      : Promise.resolve([]);

    const [company, takeovers] = await Promise.all([this.roombaGraphql.getCompany(), takeoversPromise]);

    return {
      company,
      takeovers,
    };
  }
}
